import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

export function useCreateDrawing({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
    const { t } = useTranslation('applications');
    const isOffline = useOffline();

    const mutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/technicaldrawings/create',
        {
            onSuccess: async () => {
                logger.debug('applications.drawings.create.success');
                toast.success(t('drawings.create.success'));
            },
            onError: async (error) => {
                logger.error(['applications.drawings.create.error', error]);
                toast.error(t('drawings.create.error'));
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.technicalDrawingsQueryOptions({
                        electricalProjectVersionId,
                    }),
                );
            },
        },
    );

    const disabled = useMemo(() => mutation.isPending || isOffline, [mutation.isPending, isOffline]);

    return { mutation, disabled };
}

export function useUpdateDrawing({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
    const isOffline = useOffline();

    const mutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/technicaldrawings/edit',
        {
            onSuccess: async () => {
                toast.success('Tekening bijgewerkt');
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.technicalDrawingsQueryOptions({
                        electricalProjectVersionId,
                    }),
                );
            },
        },
    );

    const disabled = useMemo(() => mutation.isPending || isOffline, [mutation.isPending, isOffline]);

    return { mutation, disabled };
}

export function useDeleteDrawing({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
    const isOffline = useOffline();

    const mutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/technicaldrawings/delete',
        {
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.technicalDrawingsQueryOptions({
                        electricalProjectVersionId,
                    }),
                );
            },
        },
    );

    const disabled = useMemo(() => mutation.isPending || isOffline, [mutation.isPending, isOffline]);

    return { mutation, disabled };
}
